import { eventsUrl, parseEntry, parseOnline, type ActivityEntry } from '@trippath/shared';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';

/** Whether the trip's changes come as they happen: they do, the stream is coming back, or the
 * device has no network. */
export type Connection = 'live' | 'connecting' | 'offline';

export interface Live {
  connection: Connection;
  /** Who has the trip open now. */
  online: { userId: string; name: string }[];
  /** Counted up at each change someone else makes: a page reads its data again when it moves. */
  version: number;
  /** Changes others made since the page opened, newest last, as they are told. */
  told: ActivityEntry[];
  /** Puts a change told away. */
  dismiss: (id: string) => void;
  /** Has the page read its data again after a change of one's own made beside it: a window over
   * the page that changed the trip. */
  refresh: () => void;
}

const NONE: Live = {
  connection: 'live',
  online: [],
  version: 0,
  told: [],
  dismiss: () => undefined,
  refresh: () => undefined,
};

const LiveContext = createContext<Live>(NONE);

/** The live state of the trip open: its stream, who is on it, what others changed. */
export function useLive(): Live {
  return useContext(LiveContext);
}

/**
 * Whether the page is seen. A tab in the background keeps no stream open: a browser has only six
 * connections to a server, and tabs each holding one would leave the tab in use none to ask with.
 */
export function useSeen(): boolean {
  const [seen, setSeen] = useState(() => document.visibilityState !== 'hidden');
  useEffect(() => {
    const look = () => {
      setSeen(document.visibilityState !== 'hidden');
    };
    document.addEventListener('visibilitychange', look);
    return () => {
      document.removeEventListener('visibilitychange', look);
    };
  }, []);
  return seen;
}

/** What is known of a trip's stream, and of which trip. */
interface Heard {
  of: string;
  connection: Connection;
  online: Live['online'];
  version: number;
  told: ActivityEntry[];
}

/** A trip's stream before anything is heard of it. */
const fresh = (trip: string): Heard => ({
  of: trip,
  connection: navigator.onLine ? 'connecting' : 'offline',
  online: [],
  version: 0,
  told: [],
});

/**
 * Listens to the open trip's stream (docs/02 §5): every change someone else makes counts the
 * version up, so the page shown reads its data again, and is told; who has it open is known; a
 * stream cut off comes back by itself, asking for what it missed. None without a trip.
 */
export function LiveTrip({
  trip,
  me,
  children,
}: {
  trip: string | null;
  me: string | null;
  children: ReactNode;
}) {
  const [heard, setHeard] = useState<Heard | null>(null);
  // What is heard of this trip; another trip's starts afresh.
  const hear = useCallback((of: string, change: (was: Heard) => Heard) => {
    setHeard((was) => change(was?.of === of ? was : fresh(of)));
  }, []);
  const dismiss = useCallback((id: string) => {
    setHeard((was) => was && { ...was, told: was.told.filter((entry) => entry.id !== id) });
  }, []);

  const seen = useSeen();
  // Seen again after a while in the background: what changed meanwhile is read again.
  const away = useRef(false);
  useEffect(() => {
    if (!seen) {
      away.current = true;
      return;
    }
    if (away.current && trip !== null) {
      hear(trip, (was) => ({ ...was, version: was.version + 1 }));
    }
    away.current = false;
  }, [seen, trip, hear]);

  useEffect(() => {
    if (trip === null || !seen) return;
    const source = new EventSource(eventsUrl(trip));
    source.addEventListener('open', () => {
      hear(trip, (was) => ({ ...was, connection: 'live' }));
    });
    source.addEventListener('error', () => {
      hear(trip, (was) => ({
        ...was,
        connection: navigator.onLine ? 'connecting' : 'offline',
      }));
    });
    source.addEventListener('online', (event) => {
      const online = parseOnline(JSON.parse(String(event.data)));
      hear(trip, (was) => ({ ...was, online }));
    });
    source.addEventListener('activity', (event) => {
      const entry = parseEntry(JSON.parse(String(event.data)));
      // One's own changes are on the page already; not those made through one's assistant.
      if (entry.via === null && entry.who !== null && entry.who.userId === me) return;
      hear(trip, (was) => ({
        ...was,
        version: was.version + 1,
        told: [...was.told.slice(-2), entry],
      }));
    });
    const down = () => {
      hear(trip, (was) => ({ ...was, connection: 'offline' }));
    };
    const up = () => {
      hear(trip, (was) => ({ ...was, connection: 'connecting' }));
    };
    window.addEventListener('offline', down);
    window.addEventListener('online', up);
    return () => {
      source.close();
      window.removeEventListener('offline', down);
      window.removeEventListener('online', up);
    };
  }, [trip, me, hear, seen]);

  const now = trip === null ? null : heard?.of === trip ? heard : fresh(trip);
  const live: Live = now
    ? {
        ...now,
        dismiss,
        refresh: () => {
          hear(now.of, (was) => ({ ...was, version: was.version + 1 }));
        },
      }
    : NONE;
  return <LiveContext.Provider value={live}>{children}</LiveContext.Provider>;
}
